import React, { useState, useEffect } from "react";
import { Box, Button, CircularProgress, Grid, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useFetchObjects from "../../../api/useFetchObjects";

const emptyForm = {
  ambulance_code: "",
  plate_number: "",
  vehicle_model: "",
  assigned_driver: "",
  driver_phone: "",
  current_location: "",
  fuel_level: "",
  odometer: "",
  last_service_date: "",
  status: "available",
  notes: "",
};

const AmbulanceForm = ({ ambulance, onSuccess, token }) => {
  const isEdit = !!ambulance;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const { data: drivers } = useFetchObjects(["drivers-list"], "drivers/", token);
  const { handleAdd, loading: addLoading, resetForm } = useAdd("ambulances", token);
  const { handleUpdate, loading: updateLoading } = useUpdate("ambulances", token, null);
  const loading = addLoading || updateLoading;

  useEffect(() => {
    if (ambulance) {
      setForm({
        ambulance_code: ambulance.ambulance_code || "",
        plate_number: ambulance.plate_number || "",
        vehicle_model: ambulance.vehicle_model || "",
        assigned_driver: ambulance.assigned_driver || "",
        driver_phone: ambulance.driver_phone || "",
        current_location: ambulance.current_location || "",
        fuel_level: ambulance.fuel_level ?? "",
        odometer: ambulance.odometer ?? "",
        last_service_date: ambulance.last_service_date || "",
        status: ambulance.status || "available",
        notes: ambulance.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [ambulance]);

  useEffect(() => {
    if (resetForm) onSuccess();
  }, [resetForm]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!form.ambulance_code.trim()) e.ambulance_code = t("ambulance.ambulanceCodeRequired");
    if (!form.plate_number.trim()) e.plate_number = t("ambulance.plateNumberRequired");
    if (!form.vehicle_model.trim()) e.vehicle_model = t("ambulance.vehicleModelRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "assigned_driver") {
      const selected = Array.isArray(drivers) && drivers.find((d) => d.id === Number(value));
      setForm((prev) => ({
        ...prev,
        assigned_driver: value,
        driver_phone: selected ? selected.phone || "" : "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "") payload[k] = v;
    });
    if (isEdit) {
      handleUpdate(ambulance.id, payload);
      setTimeout(() => onSuccess(), 1000);
    } else {
      handleAdd(payload);
    }
  };

  const tf = (label, name, type = "text") => (
    <Grid item xs={12} sm={6}>
      <TextField
        label={label}
        name={name}
        value={form[name]}
        onChange={handleChange}
        type={type}
        fullWidth
        size="small"
        error={!!errors[name]}
        helperText={errors[name]}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
      />
    </Grid>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {tf(`${t("ambulance.ambulanceCode")} *`, "ambulance_code")}
        {tf(`${t("ambulance.plateNumber")} *`, "plate_number")}
        {tf(`${t("ambulance.vehicleModel")} *`, "vehicle_model")}

        <Grid item xs={12} sm={6}>
          <TextField select label={t("ambulance.assignedDriver")} name="assigned_driver" value={form.assigned_driver} onChange={handleChange} fullWidth size="small">
            <MenuItem value="">{t("ambulance.assignedDriverNone")}</MenuItem>
            {Array.isArray(drivers) && drivers.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name} {d.phone ? `(${d.phone})` : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {tf(t("ambulance.driverPhone"), "driver_phone")}
        {tf(t("ambulance.currentLocation"), "current_location")}
        {tf(t("ambulance.fuelLevel"), "fuel_level", "number")}
        {tf(t("ambulance.odometer"), "odometer", "number")}
        {tf(t("ambulance.lastServiceDate"), "last_service_date", "date")}

        <Grid item xs={12} sm={6}>
          <TextField select label={t("ambulance.status")} name="status" value={form.status} onChange={handleChange} fullWidth size="small">
            <MenuItem value="available">{t("ambulance.available")}</MenuItem>
            <MenuItem value="on_trip">{t("ambulance.onTrip")}</MenuItem>
            <MenuItem value="maintenance">{t("ambulance.underMaintenance")}</MenuItem>
            <MenuItem value="dispatched">{t("ambulance.dispatched")}</MenuItem>
            <MenuItem value="returned">{t("ambulance.returned")}</MenuItem>
            <MenuItem value="inactive">{t("ambulance.inactive")}</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField label={t("ambulance.notes")} name="notes" value={form.notes} onChange={handleChange} fullWidth size="small" multiline rows={2} />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onSuccess} disabled={loading}>
          {t("ambulance.cancel")}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : null}>
          {isEdit ? t("ambulance.updateAmbulance") : t("ambulance.addAmbulance")}
        </Button>
      </Box>
    </Box>
  );
};

export default AmbulanceForm;
